'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Search, Send, ChevronLeft, User, Bot, HelpCircle, Check, CheckCheck, Paperclip, Loader2 } from 'lucide-react';
import { faqData } from '../../faq/faqData';

export default function SupportChatWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('faq'); // 'faq' | 'chat'

    const isOpenRef = useRef(isOpen);
    const activeTabRef = useRef(activeTab);

    useEffect(() => {
        isOpenRef.current = isOpen;
        activeTabRef.current = activeTab;
    }, [isOpen, activeTab]);

    const [searchQuery, setSearchQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [isMounted, setIsMounted] = useState(false);

    // Intake form state
    const [isRegistered, setIsRegistered] = useState(false);
    const [userName, setUserName] = useState('');
    const [userPhone, setUserPhone] = useState('');
    const [sessionId, setSessionId] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const messagesEndRef = useRef(null);
    const wsRef = useRef(null);

    // Messages state
    const [messages, setMessages] = useState([]);

    // File upload state
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !sessionId) return;

        if (file.size > 5 * 1024 * 1024) {
            alert("حجم فایل نباید بیشتر از 5 مگابایت باشد.");
            e.target.value = "";
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("sender", "user");

            const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '/api/';
            const response = await fetch(`${apiBaseUrl}chat/session/${sessionId}/attachment`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Upload failed");
            }
        } catch (error) {
            alert("خطا در ارسال فایل: " + error.message);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        setIsMounted(true);
        const savedUser = localStorage.getItem('crm-chat-user');
        const savedSessionId = localStorage.getItem('crm-chat-session-id');

        if (savedUser && savedSessionId) {
            setIsRegistered(true);
            const parsedUser = JSON.parse(savedUser);
            setUserName(parsedUser.name);
            setUserPhone(parsedUser.phone);
            setSessionId(savedSessionId);

            setIsRegistered(true);

            // Fetch Chat History
            const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '/api/';
            fetch(`${apiBaseUrl}chat/session/${savedSessionId}/history`, { cache: 'no-store' })
                .then(res => {
                    if (!res.ok) throw new Error("History fetch failed");
                    return res.json();
                })
                .then(data => {
                    if (data.messages && data.messages.length > 0) {
                        setMessages(data.messages);
                    } else {
                        // Fallback greeting if history is empty
                        setMessages([{ id: 1, text: "سلام! چطور می‌توانم کمکتان کنم؟", sender: 'agent', created_at: new Date().toISOString() }]);
                    }
                })
                .catch(err => {
                    console.error("Could not load history", err);
                    setMessages([{ id: 1, text: "سلام! چطور می‌توانم کمکتان کنم؟", sender: 'agent', created_at: new Date().toISOString() }]);
                });

            // Connect WebSocket
            connectWebSocket(savedSessionId);
        } else {
            setMessages([
                { id: 1, text: "سلام! چطور می‌توانم کمکتان کنم؟", sender: 'agent', created_at: new Date().toISOString() }
            ]);
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, []);

    // (Optional: Auto-save logic removed as backend handles history)

    // Auto-scroll chat to bottom
    useEffect(() => {
        if (activeTab === 'chat' && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, activeTab]);

    // Flatten FAQs for search and quick display
    const allQuestions = faqData.flatMap(cat => cat.questions);
    const displayedFaqs = searchQuery.trim() === ''
        ? allQuestions.slice(0, 4)
        : allQuestions.filter(q => q.question.includes(searchQuery) || q.answer.includes(searchQuery));

    const connectWebSocket = (sid) => {
        if (wsRef.current) wsRef.current.close();

        const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '/api/';
        // Ensure WebSocket URL uses ws:// or wss:// instead of http:// or https://
        let wsUrl;
        if (apiBaseUrl.startsWith('http')) {
            wsUrl = apiBaseUrl.replace(/^http/, 'ws') + `chat/ws/${sid}`;
        } else {
            // Relative path scenario fallback
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            wsUrl = `${protocol}//${window.location.host}${apiBaseUrl}chat/ws/${sid}`;
        }

        const ws = new WebSocket(wsUrl);

        let pingInterval;

        ws.onopen = () => {
            setIsConnected(true);

            // Start 10-second heartbeat ping (backend likely has a short timeout)
            pingInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    try {
                        ws.send(JSON.stringify({ type: 'ping' }));
                    } catch (e) {
                        console.error("Failed to send ping", e);
                    }
                }
            }, 10000);
        };

        ws.onclose = (event) => {
            if (pingInterval) clearInterval(pingInterval);
            console.log("WebSocket closed with code:", event.code, "reason:", event.reason);
            if (wsRef.current === ws) {
                setIsConnected(false);

                // If backend closes with 1008 (Policy Violation), session is invalid or expired
                if (event.code === 1008) {
                    localStorage.removeItem('crm-chat-user');
                    localStorage.removeItem('crm-chat-session-id');
                    localStorage.removeItem('crm-chat-messages');
                    setIsRegistered(false);
                    setSessionId(null);
                    setMessages([]);
                }
            }
        };

        const fetchHistory = async () => {
            try {
                const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '/api/';
                const response = await fetch(`${apiBaseUrl}chat/session/${sessionId}/history`, {
                    cache: 'no-store'
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.status !== 'closed') {
                        setMessages(data.messages || []);
                    }
                }
            } catch (err) {
                console.error(err);
            }
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'read_receipt') {
                    // Refetch history to get the exact true database state for is_read
                    fetchHistory();
                    return;
                }

                // Backend broadcasts all messages, including ours
                setMessages(prev => {
                    // Check to avoid duplicates if needed, but typically trust backend stream
                    if (!prev.find(m => m.id === data.id)) {
                        // If it's a message from the agent, and we are actively looking at chat, send read receipt
                        if (data.sender === 'agent' && activeTabRef.current === 'chat' && isOpenRef.current && !document.hidden) {
                            try { ws.send(JSON.stringify({ type: 'read_receipt' })); } catch (e) { }
                        }
                        return [...prev, data];
                    }
                    return prev;
                });
            } catch (e) {
                console.error("Failed to parse message", e);
            }
        };

        wsRef.current = ws;
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputValue.trim() || !wsRef.current) return;

        if (wsRef.current.readyState === WebSocket.OPEN) {
            const payload = {
                text: inputValue.trim(),
                sender: 'user'
            };

            wsRef.current.send(JSON.stringify(payload));
            setInputValue('');
        } else {
            console.warn("WebSocket is not connected yet.");
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!userName.trim() || !userPhone.trim()) return;

        // Basic Iranian phone number validation
        const phoneRegex = /^09\d{9}$/;
        if (!phoneRegex.test(userPhone.trim())) {
            alert("لطفاً یک شماره موبایل معتبر وارد کنید (مثال: 09123456789).");
            return;
        }

        try {
            const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || '/api/';
            const res = await fetch(`${apiBaseUrl}chat/session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_name: userName.trim(), user_phone: userPhone.trim() })
            });

            if (res.ok) {
                const data = await res.json();
                const sid = data.id;

                localStorage.setItem('crm-chat-user', JSON.stringify({ name: userName.trim(), phone: userPhone.trim() }));
                localStorage.setItem('crm-chat-session-id', sid);

                setSessionId(sid);
                setIsRegistered(true);
                connectWebSocket(sid);
            }
        } catch (e) {
            console.error("Failed to initialize session:", e);
        }
    };

    const toggleWidget = () => setIsOpen(!isOpen);

    // Send read receipts when widget is opened and we are in chat tab
    useEffect(() => {
        if (isOpen && activeTab === 'chat' && wsRef.current?.readyState === WebSocket.OPEN && !document.hidden) {
            setMessages(prev => {
                const hasUnread = prev.some(m => m.sender === 'agent' && !m.is_read);
                if (hasUnread) {
                    try { wsRef.current.send(JSON.stringify({ type: 'read_receipt' })); } catch (e) { }
                }
                return prev;
            });
        }
    }, [isOpen, activeTab]);

    // Send read receipts when tab comes back into focus
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && isOpen && activeTab === 'chat' && wsRef.current?.readyState === WebSocket.OPEN) {
                setMessages(prev => {
                    const hasUnread = prev.some(m => m.sender === 'agent' && !m.is_read);
                    if (hasUnread) {
                        try { wsRef.current.send(JSON.stringify({ type: 'read_receipt' })); } catch (e) { }
                    }
                    return prev;
                });
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [isOpen, activeTab]);

    if (!isMounted) return null; // Avoid hydration mismatch

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-start" dir="rtl">

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95, transformOrigin: 'bottom right' }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="mb-4 w-[90vw] md:w-[400px] h-[600px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
                    >
                        {/* Header */}
                        <div className="bg-[#6494BA] text-white p-4 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-2">
                                <Bot size={24} />
                                <div>
                                    <h3 className="font-bold flex items-center gap-2">
                                        پشتیبانی برساوش
                                        <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400 animate-pulse'}`} title={isConnected ? 'متصل' : 'در حال اتصال...'} />
                                    </h3>
                                    <p className="text-xs text-white/80">پاسخگوی پرسش های شما هستیم</p>
                                </div>
                            </div>
                            <button onClick={toggleWidget} className="p-1 hover:bg-black/10 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        {/* View Switcher */}
                        <div className="flex border-b border-gray-100 shrink-0">
                            <button
                                onClick={() => setActiveTab('faq')}
                                className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'faq' ? 'text-[#6494BA] border-b-2 border-[#6494BA]' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <HelpCircle size={16} /> پرسش های متداول
                            </button>
                            <button
                                onClick={() => setActiveTab('chat')}
                                className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'chat' ? 'text-[#6494BA] border-b-2 border-[#6494BA]' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <MessageCircle size={16} /> چت آنلاین
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-hidden flex flex-col bg-slate-50">

                            {activeTab === 'faq' ? (
                                <div className="p-4 flex-1 overflow-y-auto">
                                    <div className="relative mb-6">
                                        <input
                                            type="text"
                                            placeholder="جستجو در پرسش ها..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-4 pr-10 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#6494BA] bg-white text-sm"
                                        />
                                        <Search size={16} className="absolute right-3 top-3 text-gray-400" />
                                    </div>

                                    <div className="space-y-4">
                                        {displayedFaqs.length > 0 ? displayedFaqs.map((faq, idx) => (
                                            <div key={idx} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                                <h4 className="font-bold text-sm text-gray-800 mb-2">{faq.question}</h4>
                                                <p className="text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }}></p>
                                            </div>
                                        )) : (
                                            <p className="text-center text-sm text-gray-500 mt-10">موردی یافت نشد.</p>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => setActiveTab('chat')}
                                        className="w-full mt-6 py-3 bg-[#6494BA]/10 text-[#6494BA] font-bold rounded-xl hover:bg-[#6494BA]/20 transition-colors text-sm flex items-center justify-center gap-2"
                                    >
                                        شروع چت با پشتیبانی <ChevronLeft size={16} />
                                    </button>
                                </div>
                            ) : !isRegistered ? (
                                <div className="flex-1 flex flex-col items-center justify-center p-6">
                                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 w-full">
                                        <h4 className="text-lg font-bold text-gray-800 text-center mb-2">شروع گفتگوی آنلاین</h4>
                                        <p className="text-xs text-gray-500 text-center mb-6 leading-relaxed">برای اتصال به کارشناسان پشتیبانی، لطفاً اطلاعات زیر را تکمیل فرمایید.</p>
                                        <form onSubmit={handleRegister} className="w-full space-y-4">
                                            <div>
                                                <input
                                                    type="text"
                                                    placeholder="نام و نام خانوادگی"
                                                    value={userName}
                                                    onChange={(e) => setUserName(e.target.value)}
                                                    className="w-full bg-slate-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#6494BA] text-sm"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <input
                                                    type="tel"
                                                    placeholder="شماره تماس (مثال: 09123456789)"
                                                    value={userPhone}
                                                    onChange={(e) => setUserPhone(e.target.value)}
                                                    className="w-full bg-slate-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#6494BA] text-sm text-right"
                                                    required
                                                    pattern="^09\d{9}$"
                                                    title="شماره موبایل باید با 09 شروع شده و شامل 11 رقم باشد."
                                                    minLength={11}
                                                    maxLength={11}
                                                    dir="ltr"
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={!userName.trim() || !userPhone.trim()}
                                                className="w-full py-3 mt-2 bg-[#6494BA] text-white font-bold rounded-xl hover:opacity-90 transition-opacity text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                ورود به چت <MessageCircle size={16} />
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col h-full">
                                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                        {messages.map((msg) => {
                                            const timeString = msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }) : '';
                                            const absoluteUrl = msg.attachment_url ? `https://www.barsavosh.co${msg.attachment_url}` : null;

                                            return (
                                                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                    <div className={`max-w-[80%] rounded-2xl p-3 text-sm leading-relaxed flex flex-col ${msg.sender === 'user' ? 'bg-[#6494BA] text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'}`}>
                                                        {msg.sender === 'agent' && (
                                                            <div className="text-xs text-[#6494BA] font-bold mb-1">
                                                                پشتیبان برساوش
                                                            </div>
                                                        )}
                                                        {msg.attachment_url && (
                                                            <div className="mb-2">
                                                                <a
                                                                    href={absoluteUrl}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className={`flex items-center gap-2 p-2 rounded underline text-xs ${msg.sender === 'user' ? 'bg-black/10 text-white' : 'bg-gray-100 text-blue-600'}`}
                                                                >
                                                                    📎 دانلود فایل: {msg.text}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {!msg.attachment_url && <span>{msg.text}</span>}
                                                        <div className={`flex items-center gap-1 mt-1 ${msg.sender === 'user' ? 'text-blue-100 justify-end' : 'text-gray-400 justify-start'}`} dir="ltr">
                                                            {msg.sender === 'user' && (
                                                                <span className="opacity-80">
                                                                    {msg.is_read ? <CheckCheck size={14} /> : <Check size={14} />}
                                                                </span>
                                                            )}
                                                            {timeString && (
                                                                <span className="text-[10px]">
                                                                    {timeString}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </div>
                                    <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-gray-100 flex gap-2 shrink-0">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.txt"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={!isConnected || isUploading}
                                            className="w-10 h-10 rounded-full bg-gray-50 text-gray-500 flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                                        >
                                            {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
                                        </button>
                                        <input
                                            type="text"
                                            placeholder="پیام خود را بنویسید..."
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            className="flex-1 bg-slate-50 border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#6494BA] text-sm"
                                        />
                                        <button
                                            type="submit"
                                            disabled={!inputValue.trim() || !isConnected}
                                            className="w-10 h-10 rounded-full bg-[#6494BA] text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                                        >
                                            <Send size={18} className="rotate-270" />
                                        </button>
                                    </form>
                                </div>
                            )}

                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <button
                onClick={toggleWidget}
                className="w-14 h-14 bg-[#6494BA] text-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 hover:bg-[#5281a8] transition-all focus:outline-none focus:ring-4 focus:ring-[#6494BA]/30"
                aria-label="پشتیبانی"
            >
                <MessageCircle size={24} />
            </button>

        </div>
    );
}
